import exifr from "exifr";
import { createSHA256, type IHasher } from "hash-wasm";
import prettyBytes from "pretty-bytes";

import type { TaskResponse } from "api/model";

/** Chunk size for streaming SHA-256 so large files stay out of the JS heap. */
const FILE_HASH_CHUNK_SIZE_BYTES = 8 * 1024 * 1024;

export interface OrthoImageCoordinates {
  latitude: number;
  longitude: number;
  name: string;
}

export interface OrthoFolderQcResult {
  altitudeM: number | null;
  cmPerPx: number | null;
  coordinates: OrthoImageCoordinates[];
  flightEnd: Date | null;
  flightStart: Date | null;
  imageCount: number;
  model: string | null;
  fileSize: string;
}

export interface OrthoImageExif {
  CalibratedFocalLength?: number;
  ExifImageWidth?: number;
  FocalLength?: number;
  GPSAltitude?: number;
  Model?: string;
  RelativeAltitude?: number;
  latitude?: number;
  longitude?: number;
}

const JPEG_NAME_PATTERN = /\.(jpe?g)$/i;

/** Sensor width (mm) by DJI camera model, used when CalibratedFocalLength is absent. */
const SENSOR_WIDTH_MM_BY_MODEL: Record<string, number> = {
  FC6310: 13.2,
  FC6310S: 13.2,
};

/** webkitRelativePath (folder input) or path (Electron-style), falling back to the bare filename. */
export const getFileRelativePath = (file: File): string =>
  file.webkitRelativePath || (file as { path?: string }).path || file.name;

/** Directory portion of a file's relative path, normalized to forward slashes. */
export const getFileDirectoryPath = (file: File): string => {
  const segments = getFileRelativePath(file)
    .replaceAll("\\", "/")
    .split("/")
    .filter(Boolean);
  segments.pop();
  return segments.join("/");
};

/** Directory portion of an upload task's description (the backend joins directory path + filename), normalized to forward slashes. */
export const getTaskTopLevelFolderPath = (description: string): string => {
  const segments = description.replaceAll("\\", "/").split("/").filter(Boolean);
  return segments.length > 0 ? segments[0] : "";
};

export interface OrthoTaskFolderGroup {
  folderPath: string;
  tasks: TaskResponse[];
}

export type OrthoUploadStatus =
  "idle" | "hashing" | "uploading" | "success" | "error";

/** State for a single ortho upload/QC/resume flow, keyed by `id` so multiple can coexist. */
export interface OrthoUploadSession {
  id: string;
  /** ISO timestamp of when `errorMessage` was last set. */
  errorAt: string | null;
  errorMessage: string | null;
  /** Flight instant, including clock time. */
  flightDateTime: Date | null;
  /** Top-level folder for this session's files/tasks. */
  folderPath: string | null;
  /** Placeholder ortho this session is uploading, once `POST /orthos/` has returned. */
  orthoId: number | null;
  isAnalyzing: boolean;
  qcResult: OrthoFolderQcResult | null;
  /** Tasks being resumed, if this session was started via "Resume Upload". */
  resumeTasks: TaskResponse[] | null;
  selectedFiles: File[] | null;
  selectedSiteId: number | null;
  status: OrthoUploadStatus;
  totalFileCount: number | null;
  uploadedFileCount: number | null;
}

/** True while the session is hashing or uploading its files. */
export const isInFlightSession = (
  session: OrthoUploadSession | null,
): boolean => session?.status === "hashing" || session?.status === "uploading";

export const createOrthoUploadSession = (
  overrides?: Partial<Omit<OrthoUploadSession, "id">>,
): OrthoUploadSession => ({
  id: crypto.randomUUID(),
  errorAt: null,
  errorMessage: null,
  flightDateTime: null,
  folderPath: null,
  orthoId: null,
  isAnalyzing: false,
  qcResult: null,
  resumeTasks: null,
  selectedFiles: null,
  selectedSiteId: null,
  status: "idle",
  totalFileCount: null,
  uploadedFileCount: null,
  ...overrides,
});

/** Location UUID from a task's result summary, or null if the task hasn't produced one yet. */
export const getTaskLocationUuid = (task: TaskResponse): string | null => {
  const summary = task.results?.summary;
  return summary != null && "location_uuid" in summary
    ? summary.location_uuid
    : null;
};

/** Site ID from a task's result summary, or null if the task hasn't produced one yet. */
export const getTaskSiteId = (task: TaskResponse): number | null => {
  const summary = task.results?.summary;
  return summary != null && "site_id" in summary ? summary.site_id : null;
};

/** Ortho id from a task's result summary, or null when the task predates that field. */
export const getTaskOrthoId = (task: TaskResponse): number | null => {
  const summary = task.results?.summary;
  if (summary == null || !("ortho_id" in summary)) {
    return null;
  }
  const { ortho_id: orthoId } = summary;
  return typeof orthoId === "number" ? orthoId : null;
};

/** Calendar day (`YYYY-MM-DD`) from a task's result summary, or null if it hasn't produced one yet. */
export const getTaskFlightDate = (task: TaskResponse): string | null => {
  const summary = task.results?.summary;
  if (summary == null || !("flight_date" in summary)) {
    return null;
  }
  const { flight_date: flightDate } = summary;
  return typeof flightDate === "string" ? flightDate.slice(0, 10) : null;
};

/** Groups upload tasks by the folder derived from their description, preserving first-seen folder order. */
export const groupTasksByFolder = (
  tasks: TaskResponse[],
): OrthoTaskFolderGroup[] => {
  const tasksByTopLevelFolder = new Map<string, TaskResponse[]>();

  for (const task of tasks) {
    const folderPath = getTaskTopLevelFolderPath(task.description);
    const folderTasks = tasksByTopLevelFolder.get(folderPath);
    if (folderTasks != null) {
      folderTasks.push(task);
    } else {
      tasksByTopLevelFolder.set(folderPath, [task]);
    }
  }

  return Array.from(tasksByTopLevelFolder, ([folderPath, folderTasks]) => ({
    folderPath,
    tasks: folderTasks,
  }));
};

export const formatFileSize = (bytes: number): string => prettyBytes(bytes);

export const isZipFile = (file: File): boolean => /\.zip$/i.test(file.name);

export const isJpegEntry = (path: string): boolean => {
  const normalized = path.replaceAll("\\", "/");
  const baseName = normalized.split("/").pop() ?? "";

  return (
    JPEG_NAME_PATTERN.test(baseName) &&
    !normalized.includes("__MACOSX/") &&
    !baseName.startsWith(".")
  );
};

/**
 * Ground sample distance (cm/px) from DJI flight metadata.
 *
 * Prefer RelativeAltitude (m) + CalibratedFocalLength (px):
 *   GSD_cm/px = RelativeAltitude * 100 / CalibratedFocalLength
 *
 * Fallback: altitude + optical focal length + sensor width + image width.
 * Do not use EXIF XResolution/YResolution — those are print DPI (often 72).
 */
export const computeCmPerPx = (exif: OrthoImageExif | null): number | null => {
  if (exif == null) {
    return null;
  }

  const { RelativeAltitude: altitudeM } = exif;
  if (typeof altitudeM !== "number" || !Number.isFinite(altitudeM)) {
    return null;
  }

  const { CalibratedFocalLength: calibratedFocalLengthPx } = exif;
  if (
    typeof calibratedFocalLengthPx === "number" &&
    Number.isFinite(calibratedFocalLengthPx) &&
    calibratedFocalLengthPx !== 0
  ) {
    return (altitudeM * 100) / calibratedFocalLengthPx;
  }

  const focalLengthMm = exif.FocalLength;
  const imageWidthPx = exif.ExifImageWidth;
  const sensorWidthMm =
    typeof exif.Model === "string"
      ? SENSOR_WIDTH_MM_BY_MODEL[exif.Model]
      : undefined;

  if (
    typeof focalLengthMm !== "number" ||
    !Number.isFinite(focalLengthMm) ||
    focalLengthMm === 0 ||
    typeof imageWidthPx !== "number" ||
    !Number.isFinite(imageWidthPx) ||
    imageWidthPx === 0 ||
    typeof sensorWidthMm !== "number"
  ) {
    return null;
  }

  return (altitudeM * 100 * sensorWidthMm) / (focalLengthMm * imageWidthPx);
};

export const toOrthoImageExif = (value: unknown): OrthoImageExif | null => {
  if (value == null || typeof value !== "object") {
    return null;
  }

  const record = value as Record<string, unknown>;

  return {
    CalibratedFocalLength:
      typeof record.CalibratedFocalLength === "number"
        ? record.CalibratedFocalLength
        : undefined,
    ExifImageWidth:
      typeof record.ExifImageWidth === "number"
        ? record.ExifImageWidth
        : undefined,
    FocalLength:
      typeof record.FocalLength === "number" ? record.FocalLength : undefined,
    GPSAltitude:
      typeof record.GPSAltitude === "number" ? record.GPSAltitude : undefined,
    Model: typeof record.Model === "string" ? record.Model : undefined,
    RelativeAltitude:
      typeof record.RelativeAltitude === "number"
        ? record.RelativeAltitude
        : undefined,
    latitude: typeof record.latitude === "number" ? record.latitude : undefined,
    longitude:
      typeof record.longitude === "number" ? record.longitude : undefined,
  };
};

export const toOrthoImageCoordinates = (
  exif: OrthoImageExif,
  name: string,
): OrthoImageCoordinates | null => {
  if (typeof exif.latitude !== "number" || typeof exif.longitude !== "number") {
    return null;
  }

  return {
    latitude: exif.latitude,
    longitude: exif.longitude,
    name,
  };
};

export interface LatestFramePublisher<T> {
  cancel: () => void;
  schedule: (value: T) => void;
}

/**
 * Publishes at most one value per animation frame, always the latest scheduled.
 *
 * analyzeOrthoImages reports after every JPEG. Calling setState on each file
 * re-renders the review dialog (and Mapbox) hundreds of times. Those React
 * commits run on the main thread ahead of Mapbox onLoad, so the map only zooms
 * after every EXIF parse finishes.
 *
 * startTransition marks those updates as low-priority but still queues enough
 * work that onLoad is starved the same way. requestAnimationFrame yields to the
 * browser paint/load cycle and coalesces to about one commit per frame.
 */
export const createLatestFramePublisher = <T>(
  publish: (value: T) => void,
): LatestFramePublisher<T> => {
  let frameId: number | null = null;
  let pending: T | null = null;

  const flush = () => {
    frameId = null;
    if (pending == null) {
      return;
    }
    const value = pending;
    pending = null;
    publish(value);
  };

  const cancel = () => {
    if (frameId != null) {
      cancelAnimationFrame(frameId);
      frameId = null;
    }
    pending = null;
  };

  const schedule = (value: T) => {
    // This replaces the previous pending value if it hasn't been flushed yet.
    pending = value;
    // If a flush is not already queued, ask the browser to run one
    // before the next paint. Later schedule() calls in the same frame
    // only overwrite `pending`.
    if (frameId == null) {
      frameId = requestAnimationFrame(flush);
    }
  };

  return { cancel, schedule };
};

/** One image to analyze, decoupled from `File` so zip entries can be analyzed without extracting them. */
export interface OrthoImageSource {
  /** Bytes for exifr to parse; for a zip entry this is only the head of the image. */
  exifData: Blob;
  name: string;
  /** Full image size, which can exceed `exifData.size`. */
  size: number;
}

export const analyzeOrthoImages = async (
  images: AsyncIterable<OrthoImageSource> | Iterable<OrthoImageSource>,
  imageCount: number,
  onProgress?: (partial: OrthoFolderQcResult) => void,
): Promise<OrthoFolderQcResult> => {
  const coordinates: OrthoImageCoordinates[] = [];
  let altitudeCount = 0;
  let altitudeSum = 0;
  let cmPerPxCount = 0;
  let cmPerPxSum = 0;
  let flightEnd: Date | null = null;
  let flightStart: Date | null = null;
  let model: string | null = null;
  let totalSizeBytes = 0;

  const snapshotQcResult = (): OrthoFolderQcResult => ({
    altitudeM: altitudeCount > 0 ? altitudeSum / altitudeCount : null,
    cmPerPx: cmPerPxCount > 0 ? cmPerPxSum / cmPerPxCount : null,
    coordinates: coordinates.slice(),
    fileSize: formatFileSize(totalSizeBytes),
    flightEnd,
    flightStart,
    imageCount,
    model,
  });

  for await (const image of images) {
    totalSizeBytes += image.size;

    // Include XMP for DJI RelativeAltitude / CalibratedFocalLength.
    const rawExif: unknown = await exifr.parse(image.exifData, {
      gps: true,
      xmp: true,
    });
    const exif = toOrthoImageExif(rawExif);

    const cmPerPx = computeCmPerPx(exif);
    if (cmPerPx != null) {
      cmPerPxCount += 1;
      cmPerPxSum += cmPerPx;
    }

    if (exif != null) {
      const coordinate = toOrthoImageCoordinates(exif, image.name);
      if (coordinate != null) {
        coordinates.push(coordinate);
      }

      if (typeof exif.RelativeAltitude === "number") {
        altitudeCount += 1;
        altitudeSum += exif.RelativeAltitude;
      }

      if (model == null && typeof exif.Model === "string") {
        model = exif.Model;
      }
    }

    const record = rawExif as Record<string, unknown> | null;
    if (record != null) {
      const dto = record["DateTimeOriginal"];
      if (dto instanceof Date && !Number.isNaN(dto.getTime())) {
        if (flightStart == null || dto < flightStart) {
          flightStart = dto;
        }
        if (flightEnd == null || dto > flightEnd) {
          flightEnd = dto;
        }
      }
    }

    onProgress?.(snapshotQcResult());
  }

  return snapshotQcResult();
};

/** Adapts picked `File`s to the shape `analyzeOrthoImages` reads. */
export const filesToOrthoImages = (files: File[]): OrthoImageSource[] =>
  files.map((file) => ({
    exifData: file,
    name: file.name,
    size: file.size,
  }));

/** Hashes a single file with an already-initialized hash-wasm hasher, resetting it first so it can be reused across files. */
const hashFileWithHasher = async (
  file: File,
  hasher: IHasher,
): Promise<string> => {
  hasher.init();

  let offset = 0;
  while (offset < file.size) {
    const chunk = file.slice(offset, offset + FILE_HASH_CHUNK_SIZE_BYTES);
    const buffer = await chunk.arrayBuffer();
    hasher.update(new Uint8Array(buffer));
    offset += FILE_HASH_CHUNK_SIZE_BYTES;
  }

  return hasher.digest("hex");
};

/**
 * SHA-256 hex digests for many files, in file order. Reuses a single WASM hasher
 * instance across all files instead of instantiating one per file — folder uploads
 * can contain hundreds of images, and hashing them concurrently (one WebAssembly
 * instance each) can exhaust the browser's WASM memory.
 */
export const hashFilesSha256 = async (files: File[]): Promise<string[]> => {
  const hasher = await createSHA256();
  const digests: string[] = [];
  for (const file of files) {
    digests.push(await hashFileWithHasher(file, hasher));
  }
  return digests;
};
